/* simple-product-upsert.js
 * PDP add-to-cart upsert for simple products (no calculator).
 *
 * Problem: Shopify's /cart/add.js has add-semantics — if a variant is
 * already in the cart and the user submits the PDP form again, quantities
 * are summed. For simple products where the PDP quantity input represents
 * "how many I want in my cart", users expect the new quantity to replace
 * the existing line, not to be added on top of it.
 *
 * Fix: intercept the PDP submit in the capture phase (bypassing Impulse's
 * ProductForm via stopImmediatePropagation), then:
 *   - Find an existing line matching variant_id + selling_plan + properties
 *     (Shopify's own /cart/add merge criteria).
 *   - If found: POST /cart/change.js { id: line.key, quantity: N } (SET).
 *   - If not:   POST /cart/add.js with the original FormData (unchanged
 *     Shopify path — inventory/tracked/etc. semantics preserved).
 *
 * Scope: only form.product-single__form (Impulse's AJAX PDP form — see
 * theme.js:6897) AND only when no calculator is present on the page.
 * Calculator products have their own upsert flow in
 * product-floor-calculator.js (cartChangeByKey) and never enter here.
 *
 * After a successful write, we re-fire Impulse's exact success signal so
 * downstream listeners run: ajaxProduct:added on the form (bubbles: true)
 * — picked up by CartDrawer.init (theme.js:4391) to buildCart() + open(),
 * by fc-disable-drawer-qty, ld-cart-meta, sample-cart.logic, and the
 * calculator drawer-openers. cart:refresh on document is dispatched in
 * addition, matching the theme's established multi-companion refresh idiom
 * (sample-cart.logic.js:137, layout/theme.liquid:396).
 *
 * Pattern mirrors fc-bundle-sync.js and cart-drawer-remove.js: capture-phase
 * override of Impulse defaults with stopImmediatePropagation and a module-
 * level busy flag for double-click safety.
 */
(function () {
  'use strict';

  if (window.__simpleProductUpsertInit) return;
  window.__simpleProductUpsertInit = true;

  var FORM_SELECTOR = 'form.product-single__form';
  var CALC_SELECTOR = '#floor-calculator, #sockelleisten-calculator, #terrassendielen-calculator, .floor-calculator';

  var busy = false;

  // Extract properties[*] from FormData into a plain string-keyed object.
  function formProperties(fd) {
    var out = {};
    fd.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m) out[m[1]] = String(value);
    });
    return out;
  }

  // Normalize a properties bag for comparison. Shopify stores cart line
  // properties as strings and drops empty values on add — mirror that so
  // form-side and cart-side compare 1:1.
  function normProps(obj) {
    var out = {};
    if (!obj) return out;
    Object.keys(obj).forEach(function (k) {
      var v = obj[k];
      if (v == null) return;
      var s = String(v);
      if (s === '') return;
      out[k] = s;
    });
    return out;
  }

  function propsEqual(a, b) {
    var ka = Object.keys(a);
    if (ka.length !== Object.keys(b).length) return false;
    for (var i = 0; i < ka.length; i++) {
      var k = ka[i];
      if (a[k] !== b[k]) return false;
    }
    return true;
  }

  function sellingPlanFromCart(line) {
    return line && line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
      ? String(line.selling_plan_allocation.selling_plan.id)
      : '';
  }

  async function fetchCart() {
    var r = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!r.ok) throw new Error('Failed /cart.js (' + r.status + ')');
    return r.json();
  }

  async function cartChange(lineKey, qty) {
    var r = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: Number(qty) || 0 })
    });
    if (!r.ok) {
      var payload = null;
      try { payload = await r.json(); } catch (_) {}
      throw payload || new Error('Cart change failed: ' + r.status);
    }
    return r.json();
  }

  async function cartAddForm(form) {
    var r = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    });
    var data = null;
    try { data = await r.json(); } catch (_) {}
    if (!r.ok) throw data || new Error('Cart add failed: ' + r.status);
    return data || {};
  }

  function findExistingLine(cart, variantId, sellingPlanId, props) {
    var wantProps = normProps(props);
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (Number(it.variant_id) !== Number(variantId)) continue;
      if (sellingPlanFromCart(it) !== (sellingPlanId || '')) continue;
      if (!propsEqual(normProps(it.properties), wantProps)) continue;
      return it;
    }
    return null;
  }

  function setLoading(form, on) {
    var btn = form.querySelector('.add-to-cart, [data-add-to-cart]');
    if (!btn) return;
    if (on) btn.classList.add('btn--loading');
    else btn.classList.remove('btn--loading');
  }

  function shouldSkip(form) {
    if (!form || !form.matches || !form.matches(FORM_SELECTOR)) return true;
    // A calculator on the page owns its own cart writes (product-floor-
    // calculator.js). Never interfere.
    if (document.querySelector(CALC_SELECTOR)) return true;
    return false;
  }

  document.addEventListener('submit', async function (evt) {
    var form = evt.target;
    if (shouldSkip(form)) return;

    // Re-entry guard mirrors Impulse's own status.loading flag
    // (theme.js:2805). Second click before first resolves → drop.
    if (busy) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      return;
    }

    // We own this submit. Block Impulse's ProductForm.addItemFromForm
    // (theme.js:2802, bubble-phase listener on the form) from running.
    evt.preventDefault();
    evt.stopImmediatePropagation();

    busy = true;
    setLoading(form, true);

    try {
      var fd = new FormData(form);
      var variantId = Number(fd.get('id')) || 0;
      var quantity = Number(fd.get('quantity')) || 1;
      var sellingPlanId = String(fd.get('selling_plan') || '');
      var props = formProperties(fd);

      if (!variantId) return;

      var cart = await fetchCart();
      var existing = findExistingLine(cart, variantId, sellingPlanId, props);

      if (existing) {
        await cartChange(existing.key, quantity);
      } else {
        await cartAddForm(form);
      }

      // Post-add navigation mirrors theme.js addItemFromForm:
      //   - On the cart page section, full reload (theme.js:2837).
      //   - When theme cartType === 'page', go to /cart (theme.js:2850).
      if (document.body.classList.contains('template-cart')) {
        window.scrollTo(0, 0);
        window.location.reload();
        return;
      }
      if (window.theme && window.theme.settings && window.theme.settings.cartType === 'page') {
        window.location = (window.theme.routes && window.theme.routes.cartPage) || '/cart';
        return;
      }

      // Re-fire Impulse's success signal on the form (bubbles up to the
      // CartDrawer document listener at theme.js:4391), plus cart:refresh
      // for the LD/FC companion refresh chain.
      try {
        form.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));
      } catch (_) {}
      try {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (_) {}
    } catch (err) {
      // Match Impulse's error dispatcher shape (theme.js:2894) so any
      // listener that reacts to add-errors continues to work.
      try {
        form.dispatchEvent(new CustomEvent('ajaxProduct:error', {
          bubbles: true,
          detail: { errorMessage: (err && (err.description || err.message)) || 'Cart error' }
        }));
      } catch (_) {}
      console.warn('[simple-product-upsert]', err);
    } finally {
      setLoading(form, false);
      busy = false;
    }
  }, true); // capture phase
})();
